import { PollyClient, SynthesizeSpeechCommand } from "@aws-sdk/client-polly";
import {SSMLSplit} from "ssml-split";

const splitter = new SSMLSplit({
  synthesizer: "aws",
  breakParagraphsAboveHardLimit: true,
  extraSplitChars: ".;?!",
})

export default class TtsClient {
  /**
   * @param {{
   *  credentialProviderUrl?: string,
   *  credentials?: import("@aws-sdk/client-polly").PollyClientConfig["credentials"],
   *  outputBucket?: string
   * }} options
   */
  constructor(options) {
    /** @type{string} */
    this.credentialProviderUrl = options.credentialProviderUrl || "/rest/v1/tts_credentials"
    /** @type {import("@aws-sdk/client-polly").PollyClientConfig["credentials"] | null} */
    this.credentials = options.credentials
    this.outputBucket = options.outputBucket
    this.usesCredentialProvider = !options.credentials
  }

  /**
   * @param {AbortSignal} [signal]
   * @returns {Promise<void>}
   */
  async fetchCredentials(signal) {
    let credentials = await withAbort(withNetworkRetry(async () => {
      signal?.throwIfAborted();
      let response = await fetch(this.credentialProviderUrl, { method: "POST", signal });
      if (!response.ok) {
        throw new Error(`Unable to load text-to-speech credentials (${response.status})`);
      }
      return await response.json();
    }), signal);
    signal?.throwIfAborted();
    if (!credentials.access_key_id || !credentials.secret_access_key) {
      throw new Error("The text-to-speech credential response was incomplete");
    }

    this.credentials = {
      accessKeyId: credentials.access_key_id,
      secretAccessKey: credentials.secret_access_key,
      sessionToken: credentials.session_token
    }

    if (!this.outputBucket) {
      this.outputBucket = credentials.output_bucket
    }
  }

  /**
   * @param {string} text
   * @param {string} speed
   * @param {{signal?: AbortSignal, timeoutMs?: number}} [options]
   * @returns {Promise<{url: string, marks: object[]}[]>}
   */
  async synthesizeSpeech(text, speed, { signal: parentSignal, timeoutMs = 30000 } = {}) {
    const controller = new AbortController();
    const signal = controller.signal;
    const cancel = () => controller.abort(parentSignal?.reason);
    parentSignal?.addEventListener("abort", cancel, { once: true });
    if (parentSignal?.aborted) cancel();
    const timeout = setTimeout(() => {
      const error = new Error("Text-to-speech synthesis timed out. Please try again.");
      error.name = "TimeoutError";
      controller.abort(error);
    }, timeoutMs);

    try {
      signal.throwIfAborted();
      // Bound credentials, SDK retries AND response bodies. A fetch timeout
      // alone ends at headers and cannot protect an unfinished stream.
      return await withAbort(this.synthesizeWithSignal(text, speed, signal), signal);
    } catch (error) {
      if (this.usesCredentialProvider && error?.name == "TimeoutError") {
        this.credentials = null;
      }
      throw error;
    } finally {
      clearTimeout(timeout);
      parentSignal?.removeEventListener("abort", cancel);
      // Also stop sibling requests when one batch fails before the others.
      controller.abort();
    }
  }

  /**
   * @param {string} text
   * @param {string} speed
   * @param {AbortSignal} signal
   */
  async synthesizeWithSignal(text, speed, signal) {
    for (let attempt = 0; ; attempt += 1) {
      try {
        signal.throwIfAborted();
        if (!this.credentials) {
          await this.fetchCredentials(signal);
        }
        signal.throwIfAborted();
        return await this.performSynthesis(text, speed, signal);
      } catch (error) {
        signal.throwIfAborted();
        if (this.usesCredentialProvider && (isAuthenticationError(error) || isNetworkError(error))) {
          // A browser network error can hide the provider response. Let the
          // next Play obtain fresh credentials instead of retaining a possibly
          // expired session after all network retries have failed.
          this.credentials = null;
        }
        // Refresh expired credentials once. Other failures return to the UI
        // after their bounded retries, including a failed refresh attempt.
        if (attempt == 0 && this.usesCredentialProvider && isAuthenticationError(error)) {
          continue;
        }
        throw error;
      }
    }
  }

  /**
   * @param {string} text
   * @param {string} speed
   * @param {AbortSignal} [signal]
   * @returns {Promise<{url: string, marks: object[]}[]>}
   */
  async performSynthesis(text, speed, signal) {
    let batches = splitter.split(text);
    if (batches.length == 0) {
      throw new Error("No text was available for speech synthesis");
    }

    let client = new PollyClient({
      credentials: this.credentials ?? undefined,
      region: "us-east-1",
      maxAttempts: 3,
    });

    let promises = batches.map((batch) => {
      let wrappedText = batch.replace("<speak>", `<speak><prosody rate="${speed}">`).replace("</speak>", "</prosody></speak>");
      /** @type {import("@aws-sdk/client-polly").SynthesizeSpeechCommandInput} */
      let params = {
        Text: wrappedText,
        OutputFormat: "mp3",
        TextType: "ssml",
        VoiceId: "Joanna",
        SampleRate: "22050",
      }
      let audioProm = synthesizeBytes(client, new SynthesizeSpeechCommand(params), signal);
      let speechMarksProm = synthesizeBytes(client, new SynthesizeSpeechCommand({
        ...params, OutputFormat: "json", SpeechMarkTypes: ["word", "ssml"]
      }), signal).then((bytes) => {
        return new TextDecoder().decode(bytes).split("\n")
          .filter((line) => line.trim()).map((line) => JSON.parse(line));
      });

      // After bounded retries, preserve usable audio if timing is unavailable.
      // Log the timing failure so missing highlights can be diagnosed.
      return Promise.allSettled([audioProm, speechMarksProm]).then((results) => {
        signal?.throwIfAborted();
        if (results[0].status == "rejected") {
          throw results[0].reason;
        }

        if (results[0].value.byteLength == 0) {
          throw new Error("Polly returned empty audio");
        }

        if (results[1].status == "rejected") {
          console.warn("Text-to-speech highlight timing failed; playing audio without highlights", results[1].reason);
        }

        return {
          audio: results[0].value,
          marks: results[1].status == "fulfilled" ? results[1].value : []
        };
      });
    });

    try {
      let chunks = await withAbort(Promise.all(promises), signal);
      signal?.throwIfAborted();

      return chunks.map((chunk) => {
        let blob = new Blob([chunk.audio.slice().buffer], { type: "audio/mpeg" });
        let url = URL.createObjectURL(blob);
        return {url, marks: chunk.marks}
      });
    } finally {
      client.destroy();
    }
  }
}

/**
 * Read the response inside the SDK retry boundary. Otherwise send() succeeds
 * at the response headers and an interrupted audio body escapes SDK recovery.
 * Request and body failures share the client's maxAttempts budget.
 *
 * @param {PollyClient} client
 * @param {SynthesizeSpeechCommand} command
 * @param {AbortSignal} [signal]
 * @returns {Promise<Uint8Array>}
 */
async function synthesizeBytes(client, command, signal) {
  let bytes = new Uint8Array();
  command.middlewareStack.add((next) => async (args) => {
    let result = await next(args);
    let stream = result.output?.AudioStream;
    if (!stream) {
      throw new Error("Polly returned no audio stream");
    }
    bytes = await withAbort(stream.transformToByteArray(), signal);
    return result;
  }, { step: "deserialize", priority: "high", name: "readSpeechStream" });
  await client.send(command, { abortSignal: signal });
  return bytes;
}

/**
 * Retry the app's credential fetch, which runs outside the AWS SDK.
 * Polly requests and response streams use the SDK's own retry policy.
 *
 * @template T
 * @param {() => Promise<T>} request
 * @param {(delayMs: number) => Promise<void>} [wait]
 * @returns {Promise<T>}
 */
export async function withNetworkRetry(request, wait = delay) {
  const retryDelays = [250, 750];

  for (let attempt = 0; ; attempt += 1) {
    try {
      return await request();
    } catch (error) {
      if (!isNetworkError(error) || attempt >= retryDelays.length) {
        throw error;
      }
      await wait(retryDelays[attempt]);
    }
  }
}

/**
 * @param {number} delayMs
 */
function delay(delayMs) {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

/**
 * @param {unknown} error
 */
function isNetworkError(error) {
  if (!error || typeof error != "object") return false;
  let networkError = /** @type {{name?: string, message?: string}} */ (error);
  return networkError.name == "NetworkError" ||
    (networkError.name == "TypeError" && /fetch|network|load failed|socket/i.test(networkError.message || ""));
}

/**
 * @param {unknown} error
 */
function isAuthenticationError(error) {
  if (!error || typeof error != "object") return false;
  let errorName = /** @type {{name?: string}} */ (error).name;
  return [
    "ExpiredToken",
    "ExpiredTokenException",
    "InvalidClientTokenId",
    "InvalidSignatureException",
    "UnrecognizedClientException"
  ].includes(errorName || "");
}

/**
 * Settle even when a transport/stream does not respond to abort. Keep a
 * rejection handler on the abandoned operation so a late failure is consumed.
 *
 * @template T
 * @param {Promise<T>} operation
 * @param {AbortSignal} [signal]
 * @returns {Promise<T>}
 */
function withAbort(operation, signal) {
  if (!signal) return operation;
  return new Promise((resolve, reject) => {
    const cancel = () => {
      signal.removeEventListener("abort", cancel);
      reject(signal.reason);
    };
    signal.addEventListener("abort", cancel, { once: true });
    if (signal.aborted) cancel();
    operation.then(resolve, reject).finally(() => {
      signal.removeEventListener("abort", cancel);
    });
  });
}
